import { useEffect, useRef, useState } from "react";
import { Plus, Send } from "lucide-react";
import type {
  ApprovalStage,
  ShiftProductionForm,
  ShiftProductionFormPayload,
  ShiftFormLineUnits,
  ShiftFormDocumentSettings,
} from "../../../shared/shift-production-form/contracts";
import {
  shiftProductionDepartmentNames,
  shiftProductionLinesByDepartment,
} from "../../../shared/shift-production-form/contracts";
import { alignFormTimes } from "./form-time";
import {
  type ShiftFormValidationIssue,
  updateProduct,
  validatePayloadForSubmitIssues,
  validationMessage,
} from "./form-state";
import { emptyShiftFormPayload, ensureCurrentProductMeasurements } from "./form-payload";
import { ShiftFormDocumentInfo } from "./ShiftFormDocumentInfo";
import { ShiftFormChecklist } from "./ShiftFormChecklist";
import { ShiftFormProduct } from "./ShiftFormProduct";
import { shiftLabels } from "./line-options";
import { operatorDisplayName } from "./operator-display-name";
import { resolveMissingMeasurementUnits } from "./measurement-form-state";
import { DiscardChangesDialog } from "./DiscardChangesDialog";
import { Toast } from "../../shared/components/Toast";
import { useToastNotification } from "../../shared/components/useToastNotification";
import { type ReadOnlyRevisionDiff, useReadOnlyRevisionDiff } from "./use-read-only-revision-diff";

type PendingNavigation = { kind: "NEW" | "RECENT" };
type Props = {
  onRecentForms?: () => void;
  actorName: string;
  interactionDisabled?: boolean;
  readOnly?: boolean;
  readOnlyPayload?: ShiftProductionFormPayload | null;
  readOnlyRevisionDiff?: ReadOnlyRevisionDiff | null;
  documentSettings: Omit<
    ShiftFormDocumentSettings,
    "publicationDate" | "revisionDate"
  > & {
    publicationDate: string | null;
    revisionDate: string | null;
  };
  lineUnits: ShiftFormLineUnits;
  lineUnitsError: string | null;
  onRetryLineUnits: () => void;
  form: ShiftProductionForm | null;
  selectionVersion: number;
  busy: boolean;
  error: string | null;
  notice: string | null;
  onSave: (
    payload: ShiftProductionFormPayload,
    submit?: boolean,
  ) => Promise<ShiftProductionForm | null>;
  onNew: () => void;
};

export function ShiftFormEditor({
  onRecentForms,
  actorName,
  interactionDisabled = false,
  readOnly = false,
  readOnlyPayload = null,
  readOnlyRevisionDiff = null,
  documentSettings,
  lineUnits,
  lineUnitsError,
  onRetryLineUnits,
  form,
  selectionVersion,
  busy,
  error,
  notice,
  onSave,
  onNew,
}: Props) {
  const [payload, setPayload] = useState<ShiftProductionFormPayload>(() =>
    readOnly ? readOnlyPayload ?? form?.payload ?? editorPayload(form, actorName) : editorPayload(form, actorName),
  );
  const revision = useRef(0);
  const formRef = useRef<HTMLFormElement>(null);
  const submissionInFlight = useRef(false);
  const attemptedRevision = useRef(-1);
  const focusValidationIssue = useRef(false);
  const saveCallback = useRef(onSave);
  saveCallback.current = onSave;
  const [dirty, setDirty] = useState(false);
  const [pendingNavigation, setPendingNavigation] =
    useState<PendingNavigation | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationIssues, setValidationIssues] = useState<
    ShiftFormValidationIssue[]
  >([]);
  const toast = useToastNotification(error ?? notice, error ? "error" : "success");
  useEffect(() => {
    if (readOnly) return;
    revision.current += 1;
    attemptedRevision.current = -1;
    setPayload(editorPayload(form, actorName));
    setDirty(false);
    setValidationIssues([]);
  }, [selectionVersion, actorName, readOnly]);
  useEffect(() => {
    if (!readOnly) return;
    revision.current += 1;
    attemptedRevision.current = -1;
    setPayload(readOnlyPayload ?? form?.payload ?? editorPayload(form, actorName));
    setDirty(false);
    setValidationIssues([]);
  }, [actorName, form, readOnly, readOnlyPayload]);
  const change = (next: ShiftProductionFormPayload) => {
    if (readOnly) return;
    revision.current += 1;
    setPayload(next);
    setDirty(true);
    setValidationIssues((current) =>
      current.length ? validatePayloadForSubmitIssues(next) : current,
    );
  };
  const save = async (submit: boolean) => {
    if (readOnly) return;
    if (submissionInFlight.current) return;
    const savingRevision = revision.current;
    const prepared = resolveMissingMeasurementUnits(
      alignFormTimes(payload),
      lineUnits.find((item) => item.lineName === payload.lineName)?.speedUnit ??
        null,
    );
    if (submit) {
      const issues = [
        ...nativeValidationIssues(formRef.current),
        ...validatePayloadForSubmitIssues(prepared),
      ];
      if (issues.length) {
        focusValidationIssue.current = true;
        setValidationIssues(issues);
        return;
      }
    }
    attemptedRevision.current = savingRevision;
    if (submit) {
      submissionInFlight.current = true;
      setSubmitting(true);
    }
    try {
      const saved = await saveCallback.current(prepared, submit);
      if (saved && revision.current === savingRevision) setDirty(false);
    } finally {
      if (submit) {
        submissionInFlight.current = false;
        setSubmitting(false);
      }
    }
  };
  useEffect(() => {
    if (readOnly || !dirty || busy || attemptedRevision.current === revision.current)
      return;
    const timer = window.setTimeout(() => {
      void save(false);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [payload, dirty, busy, selectionVersion, readOnly]);
  useEffect(() => {
    if (readOnly || !dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, readOnly]);
  useEffect(() => {
    if (!validationIssues.length || !focusValidationIssue.current) return;
    focusValidationIssue.current = false;
    const target = document.querySelector<HTMLElement>(
      `[data-shift-validation="${validationIssues[0].field}"]`,
    );
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
    target?.focus({ preventScroll: true });
  }, [validationIssues]);
  useReadOnlyRevisionDiff(formRef, readOnly, readOnlyRevisionDiff);

  const fieldError = (field: string) => validationMessage(validationIssues, field);
  return (
    <form
      ref={formRef}
      className={`shift-form-workspace shift-paper${readOnly ? " shift-form-readonly" : ""}`}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void save(true);
      }}
    >
      {!readOnly ? <header className="shift-paper-toolbar">
        <div className="shift-toolbar-title">
          <h1>Vardiya üretim formu</h1>
          <div className="shift-toolbar-meta">
            <span>{form?.formNo ?? "Yeni form"}</span>
            {form?.status === "DRAFT" && form.revisions.length > 0 ? <span>Düzeltme taslağı</span> : null}
            <span role="status" aria-live="polite">
              {busy
                ? "Kaydediliyor…"
                : error && dirty
                  ? "Kaydedilemedi"
                  : dirty
                    ? "Kaydedilecek…"
                    : form
                      ? "Kaydedildi"
                      : "Değişiklikler otomatik kaydedilir"}
            </span>
            <ShiftFormDocumentInfo settings={documentSettings} collapsible />
          </div>
        </div>

        <div className="shift-toolbar-actions">
          {onRecentForms ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => requestNavigation({ kind: "RECENT" })}
            >
              Gönderdiğim formlar
            </button>
          ) : null}
          {dirty || form || error ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => requestNavigation({ kind: "NEW" })}
            >
              <Plus size={15} />
              Yeni form
            </button>
          ) : null}
          {error && dirty && !busy ? (
            <button type="button" onClick={() => void save(false)}>
              Yeniden dene
            </button>
          ) : null}
          <button type="submit" disabled={busy} className="is-primary">
            <Send size={15} />
            {form?.revisions.length ? "Yeniden onaya gönder" : "Formu gönder"}
          </button>
        </div>
      </header> : null}
      <fieldset
        className="shift-paper-body"
        data-shift-diff-path="products"
        disabled={submitting || readOnly}
        style={{ margin: 0, minWidth: 0, border: 0 }}
      >
        {readOnly ? (
          <div className="shift-pdf-heading">
            <h1>VARDİYA ÜRETİM FORMU</h1>
            <ShiftFormDocumentInfo settings={documentSettings} />
          </div>
        ) : null}
        <p className="shift-required-guidance"><strong>* Zorunlu:</strong> Formu göndermek için işaretli alanları doldurun. Eklediğiniz satırlardaki alanlar da zorunludur.</p>
        <div className="shift-paper-scroll">
          <table className="shift-paper-table shift-form-identity">
            <tbody>
              <tr>
                <th>Operatör Adı <RequiredMark /></th>
                <td>
                  <input
                    data-shift-validation="operatorName"
                    data-shift-diff-path="operatorName"
                    aria-invalid={Boolean(fieldError("operatorName"))}
                    aria-describedby={fieldError("operatorName") ? "shift-error-operator-name" : undefined}
                    required
                    readOnly
                    aria-label="Operatör Adı"
                    value={operatorDisplayName(payload.operatorName)}
                  />
                  <FieldError id="shift-error-operator-name" message={fieldError("operatorName")} />
                </td>
                <th>Tarih <RequiredMark /></th>
                <td>
                  <input
                    data-shift-validation="productionDate"
                    data-shift-diff-path="productionDate"
                    aria-invalid={Boolean(fieldError("productionDate"))}
                    aria-describedby={fieldError("productionDate") ? "shift-error-production-date" : undefined}
                    aria-label="Tarih"
                    required
                    type="date"
                    value={payload.productionDate ?? ""}
                    onChange={(event) =>
                      change({
                        ...payload,
                        productionDate: event.target.value || null,
                      })
                    }
                  />
                  <FieldError id="shift-error-production-date" message={fieldError("productionDate")} />
                </td>
                <th>Vardiya <RequiredMark /></th>
                <td>
                  <select
                    data-shift-validation="shift"
                    data-shift-diff-path="shift"
                    aria-invalid={Boolean(fieldError("shift"))}
                    aria-describedby={fieldError("shift") ? "shift-error-shift" : undefined}
                    aria-label="Vardiya"
                    required
                    value={payload.shift ?? ""}
                    onChange={(event) =>
                      change({
                        ...payload,
                        shift: event.target.value
                          ? (event.target
                              .value as ShiftProductionFormPayload["shift"])
                          : null,
                      })
                    }
                  >
                    <option value="">Seçin</option>
                    {Object.entries(shiftLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <FieldError id="shift-error-shift" message={fieldError("shift")} />
                </td>
              </tr>
              <tr>
                <th>Bölüm Adı <RequiredMark /></th>
                <td>
                  <select
                    data-shift-validation="departmentName"
                    data-shift-diff-path="departmentName"
                    aria-label="Bölüm Adı"
                    aria-invalid={Boolean(fieldError("departmentName"))}
                    aria-describedby={fieldError("departmentName") ? "shift-error-department-name" : undefined}
                    value={payload.departmentName ?? ""}
                    onChange={(event) => {
                      const departmentName = event.target.value
                        ? (event.target.value as ShiftProductionFormPayload["departmentName"])
                        : null;
                      const nextLines: readonly string[] = departmentName
                        ? shiftProductionLinesByDepartment[departmentName]
                        : [];
                      change({
                        ...payload,
                        departmentName,
                        lineName:
                          payload.lineName && nextLines.includes(payload.lineName)
                            ? payload.lineName
                            : null,
                      });
                    }}
                  >
                    <option value="">Bölüm seçin</option>
                    {shiftProductionDepartmentNames.map((department) => (
                      <option key={department} value={department}>{department}</option>
                    ))}
                  </select>
                  <FieldError id="shift-error-department-name" message={fieldError("departmentName")} />
                </td>
                <th>Makina Adı <RequiredMark /></th>
                <td>
                  <LinePicker
                    lines={payload.departmentName ? shiftProductionLinesByDepartment[payload.departmentName] : []}
                    value={payload.lineName}
                    error={fieldError("lineName")}
                    disabled={!payload.departmentName}
                    onChange={(lineName) => change({ ...payload, lineName })}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <PaperHeading title="Hat Kontrolü" />
        <ShiftFormChecklist
          items={payload.checklist}
          explanation={payload.checklistExplanation}
          validationIssues={validationIssues}
          onChange={(checklist, legacyExplanation) =>
            change({
              ...payload,
              checklist,
              checklistExplanation:
                legacyExplanation ?? payload.checklistExplanation,
            })
          }
        />
        {payload.products.map((product, index) => (
          <ShiftFormProduct
            productionDate={payload.productionDate}
            key={product.id ?? index}
            product={product}
            productIndex={index}
            speedUnit={
              lineUnits.find((item) => item.lineName === payload.lineName)
                ?.speedUnit ?? null
            }
            validationIssues={validationIssues}
            speedUnitHint={
              readOnly
                ? "—"
                : payload.lineName
                ? lineUnitsError
                  ? "Birim yüklenemedi"
                  : "Birim yükleniyor"
                : "Önce hat seçin"
            }
            onChange={(next) =>
              change(updateProduct(payload, index, () => next))
            }
          />
        ))}
        {lineUnitsError ? (
          <p className="shift-measurement-config-error" role="alert">
            {lineUnitsError}{" "}
            <button type="button" onClick={onRetryLineUnits}>
              Yeniden dene
            </button>
          </p>
        ) : null}
        <label className="shift-paper-wide-field">
          <span>Açıklama</span>
          <textarea
            aria-label="Açıklama"
            data-shift-diff-path="generalExplanation"
            placeholder="Varsa vardiyayla ilgili ek bilgi yazın"
            value={payload.generalExplanation}
            onChange={(event) =>
              change({ ...payload, generalExplanation: event.target.value })
            }
          />
        </label>
        {!readOnly ? <ApprovalFooter form={form} /> : null}
      </fieldset>
      {!readOnly ? <DiscardChangesDialog
        open={pendingNavigation !== null && !interactionDisabled}
        onCancel={() => setPendingNavigation(null)}
        onConfirm={() => {
          const action = pendingNavigation;
          setPendingNavigation(null);
          if (action) performNavigation(action);
        }}
      /> : null}
      {!readOnly && toast.notification ? (
        <Toast
          message={toast.notification.message}
          tone={toast.notification.tone}
          onDismiss={toast.dismiss}
        />
      ) : null}
    </form>
  );

  function requestNavigation(action: PendingNavigation) {
    if (dirty) setPendingNavigation(action);
    else performNavigation(action);
  }

  function performNavigation(action: PendingNavigation) {
    if (action.kind === "RECENT") onRecentForms?.();
    else if (action.kind === "NEW") {
      onNew();
      setPayload(emptyShiftFormPayload(actorName));
      setDirty(false);
    }
  }
}

function PaperHeading({ title }: { title: string }) {
  return <div className="shift-paper-heading">{title}</div>;
}
function RequiredMark() {
  return <span className="shift-required-mark" aria-label="zorunlu">*</span>;
}
function FieldError({ id, message }: { id: string; message: string | null }) {
  return message ? <small id={id} className="shift-field-error" role="alert">{message}</small> : null;
}
function nativeValidationIssues(form: HTMLFormElement | null): ShiftFormValidationIssue[] {
  if (!form) return [];
  return [...form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("input, select, textarea")]
    .filter((field) => !field.validity.valid && !field.validity.valueMissing)
    .map((field) => ({
      field: field.dataset.shiftValidation ?? field.closest<HTMLElement>("[data-shift-validation]")?.dataset.shiftValidation ?? "form",
      message: field.validity.rangeUnderflow || field.validity.rangeOverflow
        ? "Değer izin verilen aralıkta olmalıdır."
        : field.validity.stepMismatch
          ? "Geçerli bir sayı girin."
          : "Bu alanı kontrol edin.",
    }));
}
function LinePicker({
  lines,
  value,
  error,
  disabled,
  onChange,
}: {
  lines: readonly string[];
  value: ShiftProductionFormPayload["lineName"];
  error: string | null;
  disabled: boolean;
  onChange: (value: ShiftProductionFormPayload["lineName"]) => void;
}) {
  return (
    <>
      <select
        data-shift-validation="lineName"
                    data-shift-diff-path="lineName"
        aria-label="Makina Adı"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "shift-error-line-name" : undefined}
        title={value ?? "Hat seçin"}
        disabled={disabled}
        value={value ?? ""}
        onChange={(event) =>
          onChange(
            event.target.value
              ? (event.target.value as ShiftProductionFormPayload["lineName"])
              : null,
          )
        }
      >
        <option value="">{disabled ? "Önce departman seçin" : "Hat seçin"}</option>
        {value && !lines.includes(value) && <option value={value}>{value} (eski hat)</option>}
        {lines.map((line) => (
          <option key={line} value={line}>
            {line}
          </option>
        ))}
      </select>
      <FieldError id="shift-error-line-name" message={error} />
    </>
  );
}

const approvalLabels: Array<[ApprovalStage, string]> = [
  ["SHIFT_SUPERVISOR", "Vardiya Amiri"],
  ["PRODUCTION_ENGINEER", "Üretim Şefi"],
  ["PRODUCTION_MANAGER", "Üretim Müdürü"],
];
function ApprovalFooter({ form }: { form: ShiftProductionForm | null }) {
  const operatorCorrection =
    (form?.status === "DRAFT" || form?.status === "RETURNED") &&
    form.revisions.length > 0;
  return (
    <div className="shift-approval-footer">
      {approvalLabels.map(([stage, label]) => {
        const decision = form?.decisions
          .slice()
          .reverse()
          .find(
            (item) =>
              item.revisionNumber === form.revisionNumber &&
              item.stage === stage,
          );
        const pending = form?.currentApprovalStage === stage;
        return (
          <div key={stage}>
            <strong>{label}</strong>
            <span>
              {operatorCorrection
                ? "Yeniden onaya gönderilmeli"
                : decision
                  ? `${decision.actorName} · ${decision.decision === "APPROVED" ? "Onaylandı" : "Düzeltmeye gönderildi"}`
                  : pending
                    ? "Onay bekliyor"
                    : "Henüz karar yok"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function editorPayload(form: ShiftProductionForm | null, actorName: string): ShiftProductionFormPayload {
  const payload = form?.payload ?? emptyShiftFormPayload();
  return {
    ...payload,
    operatorName: actorName,
    products: payload.products.map(ensureCurrentProductMeasurements),
    generalExplanation: payload.generalExplanation || payload.products.map((product) => product.explanation).filter((text) => text.trim()).join("\n"),
  };
}
